import Big from 'big.js';
import * as lineitems from 'smg/customer-api/lineitems';
import * as html from 'smg/html';
import * as models from 'smg/models';
import * as object from 'smg/object';
import { DollarFormat } from 'smg/util';

function safeString(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }

    if (typeof value === 'function') {
        return '';
    }

    return String(value as string | number | boolean | bigint | symbol);
}

export function IsQuoteLineItem(entityLineItem: models.QuoteLineItem | models.OrderLineItem): entityLineItem is models.QuoteLineItem {
    return !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'order')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'orderID')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'orderChildLineItems')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'quoteLineItemID')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'quoteLineItem')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'orderProofID')) &&
        !(object.IsObject(entityLineItem) && object.HasKey(entityLineItem, 'orderProof'));
}

export async function Table(entityLineItems: (models.QuoteLineItem | models.OrderLineItem)[]): Promise<HTMLElement> {
    let table = `<table class="table table-striped table-hover">
        <thead>
            <tr>
                <th>Item</th>
                <th class="text-end">Qty</th>
                <th class="text-end">Price</th>
                <th class="text-end">Subtotal</th>
            </tr>
        </thead>
        <tbody>`;

    let total = Big('0');

    for (const lineItem of entityLineItems) {
        const isQuoteLineItem = IsQuoteLineItem(lineItem);

        table += `<tr class="fw-bold">
            <td>`;

        if (isQuoteLineItem && lineItem.quoteProof?.proof?.main) {
            table += `<a href="${lineItem.quoteProof.proof.main}" data-fancybox="modal" data-caption="Proof ${lineItem.quoteProof.number ?? ''}" class="pe-1">
                <img style="height:2rem" src="${lineItem.quoteProof?.proof?.thumbnail || lineItem.quoteProof.proof.main}" title="Proof ${lineItem.quoteProof.number ?? ''}" alt="Proof ${lineItem.quoteProof.number ?? ''}"></a> `;
        }

        const subtotal = Big(lineItem.price || 0).mul(lineItem.quantity || 0);
        total = total.add(subtotal);

        table += `${[
            isQuoteLineItem && lineItem.quoteProof ? `Proof ${lineItem.quoteProof.number ?? ''}` : '',
            (await lineitems.LineItemMap()).get(lineItem.lineItemID ?? '')?.pluralName || lineItem.lineItem,
            (await lineitems.TypeMap()).get(lineItem.lineItemTypeID ?? '')?.name || lineItem.type,
            (await lineitems.SizeMap()).get(lineItem.lineItemTypeSizeID ?? '')?.name || lineItem.size,
            lineItem.description,
        ].
            filter(Boolean).
            map(v => safeString(v)).
            join(' - ')}
            </td>
            <td class="text-end">
                ${(lineItem.quantity || 0).toLocaleString()}
            </td>
            <td class="text-end">
                ${DollarFormat(lineItem.price || 0)}
            </td>
            <td class="text-end">
                ${DollarFormat(subtotal)}
            </td>
        </tr>`;

        const childLineItems = (lineItem as models.QuoteLineItem).quoteChildLineItems || (lineItem as models.OrderLineItem).orderChildLineItems || [];
        for (const childLineItem of childLineItems) {
            const childSubtotal = Big(childLineItem.price || 0).mul(childLineItem.quantity || 0);
            total = total.add(childSubtotal);

            table += `<tr>
                <td class="ps-10">
                    ${[
                        (await lineitems.ChildMap()).get(childLineItem.childLineItemID ?? '')?.name || childLineItem.child,
                        (await lineitems.OptionMap()).get(childLineItem.childLineItemOptionID ?? '')?.name || childLineItem.childLineItemOption,
                        childLineItem.description,
                    ].
                        filter(Boolean).
                        map(v => safeString(v)).
                        join(' - ')}
                </td>
                <td class="text-end">
                    ${(childLineItem.quantity || 0).toLocaleString()}
                </td>
                <td class="text-end">
                    ${DollarFormat(childLineItem.price || 0)}
                </td>
                <td class="text-end">
                    ${DollarFormat(childSubtotal)}
                </td>
            </tr>`;
        }
    }

    table += `</tbody>
        <tfoot>
            <th class="text-end border-bottom-0" colspan=3>Total</th>
            <td class="text-end border-bottom-0">${DollarFormat(total)}</td>
        </tfoot>
    </table>`;

    return html.FromString(table);
}